import { useState, useEffect, useCallback } from 'react';
import { ALL_PAGES } from '../components/Layout';
import { useLang } from '../i18n/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const token = () => localStorage.getItem('token');

const PRESET_ROLES = ['admin', 'menejer', 'kassir', 'texnik'];

export default function AccessControl() {
  const { t } = useLang();
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pages, setPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [staff, setStaff] = useState([]);
  const [pinModal, setPinModal] = useState(null);
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState('');

  const configurablePages = ALL_PAGES.filter(p => !p.ownerOnly);

  const load = useCallback(async () => {
    const [rRes, sRes] = await Promise.all([
      fetch(`${API}/owner/role-permissions`, { headers: { Authorization: `Bearer ${token()}` } }),
      fetch(`${API}/staff`, { headers: { Authorization: `Bearer ${token()}` } }),
    ]);
    const rData = await rRes.json();
    const sData = await sRes.json();
    setRoles(Array.isArray(rData) ? rData : []);
    setStaff(Array.isArray(sData) ? sData : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectRole = (r) => {
    setSelected(r.role_name);
    setPages(r.pages || []);
    setMsg('');
  };

  const toggle = (slug) => {
    setPages(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    await fetch(`${API}/owner/role-permissions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ role_name: selected, pages }),
    });
    setMsg(t('savedMark'));
    await load();
    setSaving(false);
  };

  const addRole = async () => {
    const name = newRole.trim().toLowerCase();
    if (!name) return;
    await fetch(`${API}/owner/role-permissions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ role_name: name, pages: [] }),
    });
    setNewRole('');
    await load();
    setSelected(name);
    setPages([]);
  };

  const deleteRole = async (roleName) => {
    if (!window.confirm(`"${roleName}" — ${t('confirmDeleteStaff')}`)) return;
    await fetch(`${API}/owner/role-permissions/${roleName}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (selected === roleName) { setSelected(null); setPages([]); }
    await load();
  };

  const setStaffPin = async () => {
    if (!pin || pin.length < 4) return;
    const res = await fetch(`${API}/owner/staff/${pinModal.id}/pin`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ pin }),
    });
    const d = await res.json();
    if (d.ok) { setMsg(`${pinModal.name} ${t('pinSetSuccess')}`); setPinModal(null); setPin(''); }
  };

  const card = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 };

  return (
    <div>
      <h2 style={{ margin: '0 0 24px', fontSize: 20, color: 'var(--text)' }}>{t('accessTitle')}</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>

        {/* ROLLAR PANELI */}
        <div style={card}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('rolesLabel')}</div>

          {roles.map(r => (
            <div key={r.role_name} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
              background: selected === r.role_name ? 'rgba(0,212,255,0.08)' : 'var(--bg2)',
              border: `1px solid ${selected === r.role_name ? 'var(--cyan)' : 'var(--border)'}`,
            }} onClick={() => selectRole(r)}>
              <span style={{ color: selected === r.role_name ? 'var(--cyan)' : 'var(--text)', fontSize: 13, fontWeight: 600 }}>
                {r.role_name}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{(r.pages || []).length} ta</span>
                <button onClick={e => { e.stopPropagation(); deleteRole(r.role_name); }}
                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>{t('addNewRole')}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newRole} onChange={e => setNewRole(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRole()}
                placeholder={t('rolePlh')} style={{
                  flex: 1, background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '6px 10px', color: 'var(--text)', fontSize: 12
                }} />
              <button onClick={addRole} style={{
                background: 'var(--purple)', border: 'none', borderRadius: 8,
                color: '#fff', padding: '6px 12px', cursor: 'pointer', fontSize: 13
              }}>+</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
              {PRESET_ROLES.filter(r => !roles.find(x => x.role_name === r)).map(r => (
                <button key={r} onClick={() => { setNewRole(r); }} style={{
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--text2)', padding: '2px 8px',
                  fontSize: 10, cursor: 'pointer'
                }}>{r}</button>
              ))}
            </div>
          </div>
        </div>

        {/* SAHIFALAR PANELI */}
        <div>
          {selected ? (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                    "{selected}" {t('rolePagesTitle')}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                    {pages.length} {t('pagesSelected')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {msg && <span style={{ color: 'var(--green)', fontSize: 12 }}>{msg}</span>}
                  <button onClick={() => setPages(configurablePages.map(p => p.slug))} style={{
                    background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)',
                    color: 'var(--cyan)', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12
                  }}>{t('selectAllPages')}</button>
                  <button onClick={() => setPages([])} style={{
                    background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)',
                    color: 'var(--red)', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12
                  }}>{t('clearAllPages')}</button>
                  <button onClick={save} disabled={saving} style={{
                    background: 'var(--purple)', border: 'none', color: '#fff',
                    padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700
                  }}>{saving ? t('savingLabel') : t('save')}</button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                {configurablePages.map(p => {
                  const on = pages.includes(p.slug);
                  return (
                    <div key={p.slug} onClick={() => toggle(p.slug)} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                      background: on ? 'rgba(139,92,246,0.12)' : 'var(--bg2)',
                      border: `1px solid ${on ? 'var(--purple)' : 'var(--border)'}`,
                      transition: 'all 0.15s',
                    }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: 5,
                        background: on ? 'var(--purple)' : 'var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, transition: 'all 0.15s',
                      }}>
                        {on && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 14 }}>{p.icon}</span>
                      <span style={{ fontSize: 12, color: on ? 'var(--text)' : 'var(--text2)', fontWeight: on ? 600 : 400 }}>
                        {p.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ ...card, textAlign: 'center', color: 'var(--text3)', padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
              <div>{t('selectRoleHint')}</div>
            </div>
          )}

          {/* XODIMLAR VA PIN */}
          {staff.length > 0 && (
            <div style={{ ...card, marginTop: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1 }}>
                {t('staffPinTitle')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {staff.map(s => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: 'var(--bg2)', borderRadius: 10,
                    border: '1px solid var(--border)',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{s.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.role} · {s.phone}</div>
                    </div>
                    <button onClick={() => { setPinModal(s); setPin(''); }} style={{
                      background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)',
                      color: 'var(--cyan)', padding: '4px 10px', borderRadius: 6,
                      cursor: 'pointer', fontSize: 11
                    }}>PIN</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PIN MODAL */}
      {pinModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ ...card, width: 320 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>
              {pinModal.name} {t('setPinFor')}
            </div>
            <input type="password" value={pin} onChange={e => setPin(e.target.value)}
              placeholder={t('pinMinDigits')} maxLength={8}
              onKeyDown={e => e.key === 'Enter' && setStaffPin()}
              style={{
                width: '100%', background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 20,
                letterSpacing: 6, textAlign: 'center', boxSizing: 'border-box'
              }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => setPinModal(null)} style={{
                flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)',
                color: 'var(--text2)', padding: '9px', borderRadius: 8, cursor: 'pointer'
              }}>{t('cancelShort')}</button>
              <button onClick={setStaffPin} style={{
                flex: 1, background: 'var(--purple)', border: 'none',
                color: '#fff', padding: '9px', borderRadius: 8, cursor: 'pointer', fontWeight: 700
              }}>{t('setPinBtn')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
